import type {
  AdminStatsResponse,
  Clause,
  Decision,
  DecisionAudit,
  Hit,
  Network,
  NormalizedTxn,
  PatternBased,
  RuleBased,
  RuleSet,
} from '@kkp-fraud/shared';
import { hash32, rng, type Rng } from './prng';

/**
 * ~1,500 synthetic decisions over the last 24 hours, shaped like a Bangkok business day.
 *
 * Generated per absolute minute from a seeded stream, so a refresh returns the same rows with
 * the same ids and the window only slides forward with the clock. Every row is LABELLED, not
 * evaluated (Wave 2-0: the Rust engine is the only thing that evaluates rules): each scenario
 * names the rules it was drawn to trip and the decision it ends in (`SCENARIO_OUTCOME`), the
 * transaction's fields are drawn to fit, and a hit is shown only for a named rule that is in
 * the document and enabled — a scenario whose rule is gone or switched off reads as a clean
 * approval. The evidence strings are the rule's own conditions with the drawn values printed
 * in; nothing here decides whether they match.
 *
 * No real card data: card ids are opaque `tok_` tokens, customers `CIF` numbers.
 */

export const DEMO_SEED = 'kkp-dime-demo';
export const DEMO_PER_DAY = 30000;
const DAY_MINUTES = 24 * 60;
const BKK_OFFSET_MS = 7 * 3600_000;

/** Relative volume by Bangkok hour 00–23: quiet nights, lunch and evening peaks. */
const HOUR_WEIGHT = [
  0.25, 0.18, 0.12, 0.1, 0.1, 0.15, 0.35, 0.7, 1.05, 1.2, 1.3, 1.4, 1.65, 1.5, 1.25, 1.2, 1.3, 1.5, 1.7, 1.75, 1.55, 1.2,
  0.8, 0.45,
];
const MEAN_WEIGHT = HOUR_WEIGHT.reduce((a, b) => a + b, 0) / HOUR_WEIGHT.length;

export const bangkokHour = (epochMs: number): number => new Date(epochMs + BKK_OFFSET_MS).getUTCHours();

type Scenario =
  | 'clean'
  | 'alert_pb04'
  | 'notify_rb05'
  | 'step_rb02'
  | 'step_rb03'
  | 'step_pb01'
  | 'step_pb05'
  | 'decline_rb01'
  | 'decline_rb04'
  | 'decline_pb03'
  | 'decline_pb02'
  | 'step_rb02_pb01'
  | 'degraded';

/** Share of traffic, percent. APPROVE ≈ 95.4 · APPROVE_NOTIFY 1.2 · STEP_UP 2.5 · DECLINE 0.85. */
const SCENARIOS: readonly (readonly [Scenario, number])[] = [
  ['clean', 94.9],
  ['alert_pb04', 0.3],
  ['notify_rb05', 1.2],
  ['step_rb02', 0.55],
  ['step_rb03', 1.3],
  ['step_pb01', 0.3],
  ['step_pb05', 0.3],
  ['decline_rb01', 0.45],
  ['decline_rb04', 0.1],
  ['decline_pb03', 0.2],
  ['decline_pb02', 0.1],
  ['step_rb02_pb01', 0.05],
  ['degraded', 0.2],
];

/**
 * What each scenario was drawn to show: the rule-based rules it trips, whether it also trips a
 * pattern rule (added by the scenario itself, `addPattern`), and the decision the row ends in.
 */
const SCENARIO_OUTCOME: Readonly<Record<Scenario, { rules: readonly string[]; pattern: boolean; decision: Decision }>> = {
  clean: { rules: [], pattern: false, decision: 'APPROVE' },
  alert_pb04: { rules: [], pattern: true, decision: 'APPROVE' },
  notify_rb05: { rules: ['RB-05'], pattern: false, decision: 'APPROVE_NOTIFY' },
  step_rb02: { rules: ['RB-02'], pattern: false, decision: 'STEP_UP' },
  step_rb03: { rules: ['RB-03'], pattern: false, decision: 'STEP_UP' },
  step_pb01: { rules: [], pattern: true, decision: 'STEP_UP' },
  step_pb05: { rules: [], pattern: true, decision: 'STEP_UP' },
  decline_rb01: { rules: ['RB-01'], pattern: false, decision: 'DECLINE' },
  decline_rb04: { rules: ['RB-04'], pattern: false, decision: 'DECLINE' },
  decline_pb03: { rules: [], pattern: true, decision: 'DECLINE' },
  decline_pb02: { rules: [], pattern: true, decision: 'DECLINE' },
  step_rb02_pb01: { rules: ['RB-02'], pattern: true, decision: 'STEP_UP' },
  degraded: { rules: [], pattern: false, decision: 'APPROVE' },
};

interface MccProfile {
  mcc: number;
  weight: number;
  median: number;
  sigma: number;
  /** Probability the card is present. */
  cp: number;
}

/** Everyday spending — none of these can trip a rule on its own. */
const EVERYDAY: readonly MccProfile[] = [
  { mcc: 5411, weight: 20, median: 380, sigma: 0.8, cp: 0.92 },
  { mcc: 5541, weight: 8, median: 900, sigma: 0.5, cp: 0.97 },
  { mcc: 5542, weight: 3, median: 800, sigma: 0.45, cp: 1 },
  { mcc: 5812, weight: 11, median: 650, sigma: 0.7, cp: 0.85 },
  { mcc: 5814, weight: 9, median: 180, sigma: 0.6, cp: 0.75 },
  { mcc: 5311, weight: 6, median: 1500, sigma: 0.9, cp: 0.8 },
  { mcc: 5912, weight: 4, median: 420, sigma: 0.7, cp: 0.9 },
  { mcc: 5999, weight: 7, median: 700, sigma: 1.0, cp: 0.5 },
  { mcc: 4111, weight: 6, median: 45, sigma: 0.5, cp: 0.98 },
  { mcc: 4121, weight: 4, median: 160, sigma: 0.5, cp: 0.1 },
  { mcc: 6011, weight: 5, median: 0, sigma: 0, cp: 1 },
  { mcc: 5732, weight: 3, median: 6500, sigma: 0.9, cp: 0.6 },
  { mcc: 5816, weight: 3, median: 250, sigma: 0.9, cp: 0 },
  { mcc: 4814, weight: 3, median: 499, sigma: 0.4, cp: 0.1 },
  { mcc: 7832, weight: 2, median: 420, sigma: 0.4, cp: 0.5 },
  { mcc: 5691, weight: 3, median: 1300, sigma: 0.7, cp: 0.7 },
  { mcc: 7523, weight: 2, median: 60, sigma: 0.5, cp: 1 },
  { mcc: 4829, weight: 1, median: 3000, sigma: 0.9, cp: 0 },
  { mcc: 5977, weight: 2, median: 900, sigma: 0.7, cp: 0.8 },
];
const EVERYDAY_WEIGHTED = EVERYDAY.map((p) => [p, p.weight] as const);

/** Foreign merchants for everyday spending: [merchant country, currency]. */
const ABROAD: readonly (readonly [readonly [number, number], number])[] = [
  [[840, 840], 4],
  [[392, 392], 3],
  [[702, 702], 2],
  [[458, 458], 2],
  [[826, 826], 1],
  [[344, 344], 1],
  [[250, 978], 1],
];

const ATM_AMOUNTS: readonly (readonly [number, number])[] = [
  [500, 2],
  [1000, 5],
  [2000, 5],
  [3000, 3],
  [5000, 4],
  [10000, 2],
  [20000, 1],
];

const CARD_POOL = 900;

/** Mostly letters, so a token can never read as a run of card digits. */
const TOKEN_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';

/** An opaque, stable token per synthetic card — never PAN-shaped. */
export function cardToken(seed: string, i: number): string {
  const r = rng(hash32(seed, 'card', i));
  let s = '';
  for (let k = 0; k < 24; k++) s += TOKEN_ALPHABET[Math.floor(r.next() * TOKEN_ALPHABET.length)];
  return `tok_${s}`;
}

function customerOf(seed: string, i: number): string {
  return `CIF${String(hash32(seed, 'cif', i) % 100_000_000).padStart(8, '0')}`;
}

function merchantOf(r: Rng, mcc: number): string {
  return `MER${mcc}${String(r.int(0, 9999)).padStart(4, '0')}`;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const round3 = (n: number) => Math.round(n * 1000) / 1000;

type Txn = Partial<NormalizedTxn> & { network: Network; amount_thb: number; card_id: string };

interface Draft {
  scenario: Scenario;
  txn: Txn;
  extra: Hit[];
  degradedReason?: string;
}

function pb(ruleSet: RuleSet, id: string): PatternBased | undefined {
  return (ruleSet.patternBased ?? []).find((r) => r.id === id && r.enabled);
}

function rb(ruleSet: RuleSet, id: string): RuleBased | undefined {
  return (ruleSet.ruleBased ?? []).find((r) => r.id === id && r.enabled);
}

// ---- evidence text — how the engine prints a hit's `detail`; formatting only -------------

function fmt(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (Array.isArray(v)) return `[${v.map(fmt).join(',')}]`;
  if (typeof v === 'string') return `'${v}'`;
  return String(v);
}

const isSetClause = (c: Clause): c is Clause & { value: string } => (c.op === 'in_set' || c.op === 'not_in_set') && typeof c.value === 'string';

/** Each condition with the drawn value printed in: `mcc=5999 nin [5411,5541]`. */
function explainClauses(clauses: readonly Clause[] | undefined, txn: Txn): string {
  if (!clauses || clauses.length === 0) return 'no filter';
  return clauses
    .map((c, i) => {
      const actual = (txn as Record<string, unknown>)[c.field as string];
      const part = `${String(c.field)}=${fmt(actual ?? null)} ${c.op} ${isSetClause(c) ? c.value : fmt(c.value)}`;
      return i === 0 ? part : `${c.logic ?? 'AND'} ${part}`;
    })
    .join(' ');
}

/** A pattern rule's evidence for a window that reached `value` over `matched` events. */
function patternDetail(rule: PatternBased, scopeValue: string, value: number, matched: number): string {
  const agg =
    rule.aggregate === 'count'
      ? 'count'
      : `${rule.aggregate}(${String(rule.aggregateField ?? (rule.aggregate === 'sum' ? 'amount_thb' : 'card_id'))})`;
  const filters =
    rule.filters && rule.filters.length > 0
      ? `; filters ${rule.filters.map((c) => `${String(c.field)} ${c.op} ${isSetClause(c) ? c.value : JSON.stringify(c.value)}`).join(' AND ')}`
      : '';
  return (
    `${agg} of ${rule.event} on ${rule.scope}=${scopeValue} over ${rule.windowSeconds}s = ${value} >= ${rule.threshold} ` +
    `(${matched} event${matched === 1 ? '' : 's'} incl. current${filters})`
  );
}

/**
 * The row's hits and decision from its scenario's label. Every rule the scenario names must be
 * in the document and enabled; otherwise the row is a clean approval with no hits.
 */
function labelled(d: Draft, ruleSet: RuleSet): { hits: Hit[]; decision: Decision } {
  const outcome = SCENARIO_OUTCOME[d.scenario];
  const rules = outcome.rules.map((id) => rb(ruleSet, id));
  if (rules.some((r) => r === undefined) || (outcome.pattern && d.extra.length === 0)) {
    return { hits: [], decision: 'APPROVE' };
  }
  const hits: Hit[] = [
    ...rules.map((r) => ({ rule_id: r!.id, name: r!.name, action: r!.action, detail: explainClauses(r!.clauses, d.txn) })),
    ...d.extra,
  ];
  return { hits, decision: outcome.decision };
}

function everyday(r: Rng, t: number, seed: string, network: Network): Txn {
  const p = r.weighted(EVERYDAY_WEIGHTED);
  const cardIdx = r.int(0, CARD_POOL - 1);
  const isAtm = p.mcc === 6011;
  const cp = isAtm || r.chance(p.cp);
  let amount = isAtm ? r.weighted(ATM_AMOUNTS) : round2(Math.max(20, r.logNormal(p.median, p.sigma)));
  // Foreign spend is mostly online; ATMs, transit, parking and money transfer stay domestic here.
  const canTravel = !isAtm && ![4111, 7523, 4829, 5542].includes(p.mcc);
  const [country, currency] = canTravel && r.chance(cp ? 0.02 : 0.1) ? r.weighted(ABROAD) : [764, 764];
  if (!cp) amount = Math.min(amount, r.chance(0.97) ? 15000 : 60000);
  const threeDs = !cp && (amount > 15000 || r.chance(0.82));
  const unattended = cp && (p.mcc === 5542 || p.mcc === 7523);
  if (unattended) amount = Math.min(amount, 4000);
  return {
    card_id: cardToken(seed, cardIdx),
    customer_id: customerOf(seed, cardIdx),
    network,
    mcc: p.mcc,
    amount_thb: amount,
    currency_code_num: currency,
    merchant_country_num: country,
    merchant_id: merchantOf(r, p.mcc),
    card_present: cp,
    ecommerce: !cp,
    three_ds_authenticated: threeDs,
    entry_mode: !cp ? '81' : r.weighted([['07', 55], ['05', 43], ['90', 2]] as const),
    attended: cp ? !unattended : null,
    is_atm: isAtm,
    txn_type: isAtm ? 'atm_withdrawal' : 'purchase',
    local_hour: bangkokHour(t),
    // Mastercard carries the scheme score (DE 48.75); ITMX never does, so RB-01 is skipped there.
    fraud_score:
      network === 'MASTERCARD'
        ? r.chance(0.012) && [5411, 5541, 4111].includes(p.mcc)
          ? r.int(705, 880)
          : Math.min(690, Math.round(r.logNormal(90, 0.9)))
        : null,
  };
}

const lowScore = (r: Rng, network: Network) => (network === 'MASTERCARD' ? r.int(20, 560) : null);

function draft(scenario: Scenario, r: Rng, t: number, seed: string, ruleSet: RuleSet): Draft {
  let network: Network = r.chance(0.4) ? 'MASTERCARD' : 'ITMX';
  if (scenario === 'decline_rb01') network = 'MASTERCARD';
  const txn = everyday(r, t, seed, network);
  const extra: Hit[] = [];
  const cnp = (amount: number, threeDs: boolean) =>
    Object.assign(txn, {
      card_present: false,
      ecommerce: true,
      entry_mode: '81',
      attended: null,
      is_atm: false,
      txn_type: 'purchase' as const,
      three_ds_authenticated: threeDs,
      amount_thb: round2(amount),
    });
  const foreign = (pairs: readonly (readonly [readonly [number, number], number])[]) => {
    const [country, currency] = r.weighted(pairs);
    Object.assign(txn, { merchant_country_num: country, currency_code_num: currency });
  };
  const addPattern = (id: string, scopeValue: string, value: number, matched: number) => {
    const rule = pb(ruleSet, id);
    if (rule) extra.push({ rule_id: rule.id, name: rule.name, action: rule.action, detail: patternDetail(rule, scopeValue, value, matched) });
  };

  switch (scenario) {
    case 'clean':
      break;
    case 'alert_pb04': {
      const distinct = r.int(20, 27);
      addPattern('PB-04', String(txn.merchant_id), distinct, distinct + r.int(0, 6));
      break;
    }
    case 'notify_rb05': {
      const mcc = r.pick([5542, 7523, 5814]);
      Object.assign(txn, {
        mcc,
        merchant_id: merchantOf(r, mcc),
        card_present: true,
        ecommerce: false,
        entry_mode: r.pick(['05', '07']),
        attended: false,
        three_ds_authenticated: false,
        currency_code_num: 764,
        merchant_country_num: 764,
        amount_thb: round2(10_050 + r.logNormal(3500, 0.8)),
        fraud_score: lowScore(r, network),
      });
      break;
    }
    case 'step_rb02': {
      const mcc = r.weighted([[7995, 3], [6051, 2], [4829, 2]] as const);
      Object.assign(txn, { mcc, merchant_id: merchantOf(r, mcc), fraud_score: lowScore(r, network) });
      cnp(Math.min(18_500, r.logNormal(3500, 0.8)), true);
      foreign([[[840, 840], 5], [[470, 978], 2], [[344, 344], 2]]);
      break;
    }
    case 'step_rb03': {
      const mcc = r.pick([5732, 4722, 5944, 5311]);
      Object.assign(txn, { mcc, merchant_id: merchantOf(r, mcc), fraud_score: lowScore(r, network) });
      cnp(20_100 + r.logNormal(12_000, 0.7), false);
      if (r.chance(0.25)) foreign([[[840, 840], 3], [[702, 702], 1], [[392, 392], 1]]);
      else Object.assign(txn, { merchant_country_num: 764, currency_code_num: 764 });
      break;
    }
    case 'step_pb01': {
      Object.assign(txn, { mcc: 5816, merchant_id: merchantOf(r, 5816), fraud_score: lowScore(r, network) });
      cnp(150 + r.logNormal(400, 0.7), true);
      foreign([[[840, 840], 4], [[250, 978], 1], [[344, 344], 2]]);
      const n = r.int(3, 4);
      addPattern('PB-01', txn.card_id, n, n);
      break;
    }
    case 'step_pb05': {
      // A night-time chain only exists at night; by day the same share goes to RB-03.
      if ((txn.local_hour ?? 12) > 5) return draft('step_rb03', r, t, seed, ruleSet);
      const mcc = r.pick([5411, 5814, 5541, 5999]);
      Object.assign(txn, {
        mcc,
        merchant_id: merchantOf(r, mcc),
        card_present: true,
        ecommerce: false,
        entry_mode: '07',
        attended: true,
        three_ds_authenticated: false,
        currency_code_num: 764,
        merchant_country_num: 764,
        amount_thb: round2(80 + r.logNormal(450, 0.6)),
        fraud_score: lowScore(r, network),
      });
      const n = r.int(4, 6);
      addPattern('PB-05', txn.card_id, n, n);
      break;
    }
    case 'decline_rb01': {
      const mcc = r.pick([5999, 5732, 5816, 4829, 5944, 5311]);
      Object.assign(txn, { mcc, merchant_id: merchantOf(r, mcc), fraud_score: r.int(705, 985) });
      if (r.chance(0.7)) {
        cnp(r.logNormal(4200, 1.0), true);
        foreign([[[840, 840], 3], [[826, 826], 1], [[702, 702], 1]]);
      }
      break;
    }
    case 'decline_rb04': {
      const mcc = r.pick([5999, 5311, 5732, 5691]);
      Object.assign(txn, {
        mcc,
        merchant_id: merchantOf(r, mcc),
        card_present: true,
        ecommerce: false,
        attended: true,
        three_ds_authenticated: false,
        entry_mode: r.pick(['90', '80', '02']),
        amount_thb: round2(r.logNormal(5200, 0.8)),
        fraud_score: lowScore(r, network),
      });
      foreign([[[116, 840], 3], [[104, 840], 2], [[704, 704], 2], [[360, 360], 1]]);
      break;
    }
    case 'decline_pb03': {
      const mcc = r.pick([5816, 5999, 4816]);
      Object.assign(txn, { mcc, merchant_id: merchantOf(r, mcc), fraud_score: lowScore(r, network) });
      cnp(r.pick([1, 10, 20, 35, 49, 12.5]), false);
      foreign([[[840, 840], 4], [[826, 826], 1]]);
      const n = r.int(5, 9);
      addPattern('PB-03', txn.card_id, n, n);
      break;
    }
    case 'decline_pb02': {
      Object.assign(txn, {
        mcc: 6011,
        merchant_id: merchantOf(r, 6011),
        card_present: true,
        ecommerce: false,
        attended: true,
        is_atm: true,
        txn_type: 'atm_withdrawal',
        entry_mode: '05',
        three_ds_authenticated: false,
        amount_thb: r.pick([8000, 10000, 12000, 15000, 20000]),
        fraud_score: lowScore(r, network),
      });
      foreign([[[116, 840], 3], [[704, 704], 1], [[418, 840], 1]]);
      const events = r.int(3, 4);
      const sum = txn.amount_thb + (events - 1) * r.pick([10000, 12000, 15000]);
      addPattern('PB-02', txn.card_id, Math.max(sum, 30_000), events);
      break;
    }
    case 'step_rb02_pb01': {
      // RB-02 and PB-01 (both STEP_UP) on one transaction: the strongest action is STEP_UP —
      // nothing accumulates (no scoring since 30 Sep 2026).
      Object.assign(txn, { mcc: 7995, merchant_id: merchantOf(r, 7995), fraud_score: lowScore(r, network) });
      cnp(r.logNormal(2500, 0.6), true);
      foreign([[[840, 840], 3], [[344, 344], 1]]);
      const n = r.int(3, 5);
      addPattern('PB-01', txn.card_id, n, n);
      break;
    }
    case 'degraded':
      return {
        scenario,
        txn,
        extra,
        degradedReason: r.weighted([
          ['evaluation exceeded FAILSAFE_TIMEOUT_MS=200', 3],
          ['idempotency lookup exceeded FAILSAFE_TIMEOUT_MS=200', 1],
          ['state store unavailable: Connection timeout', 1],
        ] as const),
      };
  }
  return { scenario, txn, extra };
}

function failSafe(txn: Txn, ruleSet: RuleSet, reason: string): { decision: Decision; hit: Hit } {
  const policy = ruleSet.failSafe ?? { cardPresentAction: 'APPROVE', ecommerceAction: 'STEP_UP', ecommerceMinAmountThb: 20000 };
  const decision: Decision = txn.card_present
    ? policy.cardPresentAction
    : txn.ecommerce && txn.amount_thb >= policy.ecommerceMinAmountThb
      ? policy.ecommerceAction
      : 'APPROVE';
  return {
    decision,
    hit: {
      rule_id: 'FAIL_SAFE',
      name: 'Fail-safe policy (REQ-09)',
      action: decision,
      detail:
        `${reason}; card_present=${txn.card_present} ecommerce=${txn.ecommerce} ` +
        `amount_thb=${txn.amount_thb} (e-com floor ${policy.ecommerceMinAmountThb}) → ${decision}`,
    },
  };
}

/**
 * A v4-shaped id that is unique BY CONSTRUCTION: the first two groups carry the minute and the
 * row's index. The PRNG is mulberry32 (32-bit state), so at tens of thousands of rows a
 * day two minutes' streams can overlap and random ids would repeat.
 */
function rowId(kind: 'id' | 'txn', minute: number, k: number, seed: string): string {
  const h = (n: number) => hash32(seed, kind, minute, k, n).toString(16).padStart(8, '0');
  const head = h(0) + h(1);
  const variant = ((parseInt(head[12]!, 16) & 0x3) | 0x8).toString(16);
  // Unique part first (minute · index), random part last — screens show the tail of an id.
  const lead = minute.toString(16).padStart(8, '0').slice(-8);
  const idx = k.toString(16).padStart(4, '0').slice(-4);
  return `${lead}-${idx}-4${head.slice(13, 16)}-${variant}${h(2).slice(0, 3)}-${head.slice(0, 12)}`;
}

function audit(r: Rng, t: number, d: Draft, ruleSet: RuleSet, ruleSetVersion: string, ids: { id: string; txn: string }): DecisionAudit {
  const { txn } = d;
  let decision: Decision;
  let hits: Hit[];
  let engineLatencyMs: number;
  let serviceLatencyMs: number;
  if (d.degradedReason) {
    const fs = failSafe(txn, ruleSet, d.degradedReason);
    decision = fs.decision;
    hits = [fs.hit];
    engineLatencyMs = 0;
    serviceLatencyMs = round3(200 + r.logNormal(4, 0.6));
  } else {
    ({ hits, decision } = labelled(d, ruleSet));
    // Engine p50 ≈ 2.3 ms, p95 ≈ 3.7 ms; the service adds parsing, the state read and the audit hand-off.
    engineLatencyMs = round3(Math.min(40, r.logNormal(2.3, 0.289)));
    serviceLatencyMs = round3(engineLatencyMs + 0.35 + r.logNormal(0.45, 0.5));
  }
  return {
    id: ids.id,
    cardId: txn.card_id,
    customerId: txn.customer_id ?? null,
    transactionId: ids.txn,
    eventTimeUtc: new Date(t).toISOString(),
    decision,
    reasons: hits.map((h) => h.rule_id),
    hits,
    degraded: Boolean(d.degradedReason),
    shadowMode: false,
    engineVersion: 'poc-1.0',
    ruleSetVersion,
    engineLatencyMs,
    serviceLatencyMs,
    network: txn.network,
    amountThb: txn.amount_thb,
    mcc: txn.mcc ?? null,
    merchantCountryNum: txn.merchant_country_num ?? null,
  };
}

export interface DemoDecisionOptions {
  /** The rule document the rows are evaluated against (the live one when it could be read). */
  ruleSet: RuleSet;
  /** Stamped on every row; defaults to the document's own version. */
  ruleSetVersion?: string;
  now?: number;
  seed?: string;
  perDay?: number;
}

/** Rows for one absolute minute — the unit that keeps ids stable while the window slides. */
function minuteRows(minute: number, seed: string, perDay: number, ruleSet: RuleSet, version: string): DecisionAudit[] {
  const r = rng(hash32(seed, 'minute', minute));
  const start = minute * 60_000;
  const lambda = (perDay / DAY_MINUTES) * (HOUR_WEIGHT[bangkokHour(start)]! / MEAN_WEIGHT);
  const n = r.poisson(lambda);
  const out: DecisionAudit[] = [];
  for (let k = 0; k < n; k++) {
    const t = start + r.int(0, 59_999);
    const scenario = r.weighted(SCENARIOS);
    out.push(audit(r, t, draft(scenario, r, t, seed, ruleSet), ruleSet, version, { id: rowId('id', minute, k, seed), txn: rowId('txn', minute, k, seed) }));
  }
  return out;
}

/** Generated minutes, keyed by minute + inputs. Bounded: a day and a bit of minutes per input set. */
const minuteCache = new Map<string, DecisionAudit[]>();

/** The last 24 hours of demo decisions, newest first. Each minute is generated once, so a 5 s refresh is cheap. */
export function demoDecisions(opts: DemoDecisionOptions): DecisionAudit[] {
  const now = opts.now ?? Date.now();
  const seed = opts.seed ?? DEMO_SEED;
  const perDay = opts.perDay ?? DEMO_PER_DAY;
  const version = opts.ruleSetVersion ?? opts.ruleSet.version;
  const inputs = `${seed}|${perDay}|${version}|${opts.ruleSet.version}|${opts.ruleSet.updatedUtc ?? ''}`;
  if (minuteCache.size > 2 * DAY_MINUTES) minuteCache.clear();
  const nowMinute = Math.floor(now / 60_000);
  const rows: DecisionAudit[] = [];
  for (let m = nowMinute - DAY_MINUTES + 1; m <= nowMinute; m++) {
    const key = `${m}|${inputs}`;
    let minute = minuteCache.get(key);
    if (!minute) {
      minute = minuteRows(m, seed, perDay, opts.ruleSet, version);
      minuteCache.set(key, minute);
    }
    for (const row of minute) if (Date.parse(row.eventTimeUtc) <= now) rows.push(row);
  }
  rows.sort((a, b) => (a.eventTimeUtc < b.eventTimeUtc ? 1 : a.eventTimeUtc > b.eventTimeUtc ? -1 : 0));
  return rows;
}

/** Nearest-rank percentile — the decision API's `stats.ts`, so the tiles read the same way. */
export function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  const rank = Math.ceil((p / 100) * sorted.length);
  const idx = Math.min(sorted.length - 1, Math.max(0, rank - 1));
  return Number(sorted[idx]!.toFixed(3));
}

/** `/admin/stats` computed from the same rows, exactly as the decision API computes it. */
export function demoStats(rows: readonly DecisionAudit[]): AdminStatsResponse {
  const byDecision: Record<Decision, number> = { APPROVE: 0, APPROVE_NOTIFY: 0, STEP_UP: 0, DECLINE: 0 };
  const byRule: Record<string, number> = {};
  let degraded = 0;
  let since = '';
  const latencies: number[] = [];
  for (const a of rows) {
    if (a.decision in byDecision) byDecision[a.decision]++;
    for (const id of a.reasons) byRule[id] = (byRule[id] ?? 0) + 1;
    if (a.degraded) degraded++;
    if (Number.isFinite(a.serviceLatencyMs)) latencies.push(a.serviceLatencyMs);
    if (since === '' || a.eventTimeUtc < since) since = a.eventTimeUtc;
  }
  latencies.sort((x, y) => x - y);
  return {
    since: since || new Date().toISOString(),
    total: rows.length,
    byDecision,
    byRule,
    degraded,
    p50Ms: percentile(latencies, 50),
    p95Ms: percentile(latencies, 95),
    p99Ms: percentile(latencies, 99),
  };
}
